import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useLayoutEffect,
} from "react";
import { useSelector } from "react-redux";
import DownIcon from "./down.js";
import Graph from "./slopeChart.js";
import BubbleChart from "../bubble_chart";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { useReactToPrint } from "react-to-print";
import Line from "./line.js";
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const MainReport = () => {
  const themeMode = useSelector((state) => state.themeMode);
  const chartRef = useRef(null);
  const topMenuTabs = useSelector((state) => state.topMenuTabs);

  const leftMenuVisibility = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.leftMenuVisibility;

  const currentSurveyInsightData = useMemo(() => {
    const activeTab = topMenuTabs.tabs.find(
      (tab) => tab.id === topMenuTabs.activeTab
    );
    return activeTab ? activeTab.states.currentSurveyInsightData : null;
  }, [topMenuTabs.tabs, topMenuTabs.activeTab]);

  const currentSurvey = useMemo(() => {
    const activeTab = topMenuTabs.tabs.find(
      (tab) => tab.id === topMenuTabs.activeTab
    );
    return activeTab ? activeTab.states.currentSurvey : null;
  }, [topMenuTabs.tabs, topMenuTabs.activeTab]);

  const DEFAULT_SCORING = {
    maxScore: 5,
    stages: [
      { id: "s1", name: "Initial", description: "", from: 1, to: 1.5 },
      { id: "s2", name: "Under Development", description: "", from: 1.5, to: 2.5 },
      { id: "s3", name: "Defined", description: "", from: 2.5, to: 3.5 },
      { id: "s4", name: "Managed", description: "", from: 3.5, to: 4.5 },
      { id: "s5", name: "Measured", description: "", from: 4.5, to: 5 },
    ],
  };

  const scoringConfig = (currentSurvey && currentSurvey.scoring) ? currentSurvey.scoring : DEFAULT_SCORING;
  const scoringMaxScore = scoringConfig.maxScore || 5;
  const scoringCoefficient = scoringMaxScore / 5;

  const getDisplayScore = (internalScore) => internalScore * scoringCoefficient;

  const getActiveStage = (internalScore) => {
    const displayScore = getDisplayScore(internalScore);
    const stageList = scoringConfig.stages || [];
    const found = stageList.find(
      (s) => displayScore >= s.from && displayScore < s.to
    );
    return found || stageList[stageList.length - 1] || { name: "", description: "" };
  };

  const [strengths, setStrengths] = useState([]);
  const [weaknesses, setWeaknesses] = useState([]);
  const [leftPercentage, setLeftPercentage] = useState(0);
  const [stages, setStages] = useState(
    (scoringConfig.stages || []).map((s) => ({ name: s.name, active: false }))
  );
  const [refsReady, setRefsReady] = useState(false); // Ref'lerin hazır olup olmadığını kontrol etmek için
  let keyFindingsGlobalIndex = 0;
  let recommendationGlobalIndex = 0;
  let bubbleDetailCardsGlobalIndex = 0;
  const setRef = (index, element) => {
    if (element) {
      recommendationRowsRef.current[index] = element;
    }

    // Tüm ref'ler yerleştiğinde işlemi başlat
    if (
      Object.keys(recommendationRowsRef.current).length === mappedData.length
    ) {
      setRefsReady(true);
    }
  };
  const groupedFindings =
    currentSurveyInsightData?.report_detail?.findings?.reduce((acc, item) => {
      if (!acc[item.section_name]) {
        acc[item.section_name] = [];
      }
      acc[item.section_name].push(item);
      return acc;
    }, {});

  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const [scrolling, setScrolling] = useState(false);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // Tolerans payı
      setScrolling(isAtBottom);
    }
  };

  useEffect(() => {
    if (currentSurveyInsightData?.report_detail?.strengths) {
      setStrengths([...currentSurveyInsightData.report_detail.strengths]); // Spread kullanarak yeni array oluştur
    } else {
      setStrengths([]);
    }
    if (currentSurveyInsightData?.report_detail?.weaknesses) {
      setWeaknesses([...currentSurveyInsightData.report_detail.weaknesses]); // Spread kullanarak yeni array oluştur
    } else {
      setWeaknesses([]);
    }

    if (
      currentSurveyInsightData &&
      currentSurveyInsightData?.report_detail?.scores?.overall
    ) {
      const internalOverall = currentSurveyInsightData.report_detail.scores.overall;
      const displayScore = getDisplayScore(internalOverall);
      setLeftPercentage((displayScore / scoringMaxScore) * 100);

      const activeStage = getActiveStage(internalOverall);
      const newStages = (scoringConfig.stages || []).map((s) => ({
        name: s.name,
        active: s.name === activeStage.name,
      }));
      setStages(newStages);
    }
  }, [currentSurveyInsightData.report_detail, currentSurvey]);

  const mappedData = (currentSurveyInsightData?.report_detail?.findings ?? []).reduce(
    (acc, finding) => {
      const sectionName = finding.section_name;
      const existingSection = acc.find(
        (item) => item.section_name === sectionName
      );
      const relatedRecommendations =
        (currentSurveyInsightData?.report_detail?.recommendations ?? []).filter(
          (rec) => rec.finding_id === finding.finding_id
        );

      if (existingSection) {
        existingSection.recommendations.push(...relatedRecommendations);
      } else {
        acc.push({
          section_name: sectionName,
          recommendations: [...relatedRecommendations],
        });
      }

      return acc;
    },
    []
  );

  const getColorForIndex = (index, total) => {
    const ratio = index / (total - 1);
    const r = Math.round(106 + ratio * (0 - 106));
    const g = Math.round(10 + ratio * (0 - 10));
    const b = Math.round(173 + ratio * (255 - 173));

    return `rgb(${r}, ${g}, ${b})`;
  };

  const steps = [
    {
      number: "01",
      title: "ASSESS THE BENEFIT",
      description:
        "Assess the benefit of each initiative in organizational context based on stakeholder inputs and Vision Workshop feedback",
      bgColor: "bg-[#44a5e4]",
    },
    {
      number: "02",
      title: "ESTIMATE THE EFFORT",
      description:
        "Effort is estimated based on potential complexity of the task, funding and stakeholder experience based on past efforts to solve a finding",
      bgColor: "bg-[#44a5e4]",
    },
    {
      number: "03",
      title: "PLAN FOR DEPENDENCIES",
      description:
        "Dependencies have been accounted for based on information available. Initiatives dependent on the completion of other initiatives or initiatives which need a longer planning phase have been identified as long-term projects",
      bgColor: "bg-[#44a5e4]",
    },
  ];

  const findings = currentSurveyInsightData?.report_detail?.findings || [];
  const prevGroupedByType = useRef(null);
  const [groupedByType, setGroupedByType] = useState({});
  useEffect(() => {
    if (currentSurveyInsightData?.report_detail?.recommendations) {
      const newGroupedByType =
        currentSurveyInsightData.report_detail.recommendations.reduce(
          (acc, item) => {
            const type = item.type || "Unknown"; // Type boşsa "Unknown" olarak ata

            if (!acc[type]) {
              acc[type] = {}; // Type'a göre boş obje oluştur
            }

            const key = `${item.benefit}-${item.ease_of_implementation}`; // Grup anahtarı

            // Finding ID ile eşleşen findings'leri bul ve benzersiz section'ları al
            const matchingSections = [
              ...new Set(
                findings
                  .filter((finding) => finding.finding_id === item.finding_id)
                  .map((finding) => finding.section_name)
              ),
            ];

            // Eğer recommendation varsa ekleyelim
            if (item.recommendation) {
              if (!acc[type][key]) {
                acc[type][key] = {
                  benefit: item.benefit,
                  ease_of_implementation: item.ease_of_implementation,
                  benefits: [...item.benefits],
                  risk_and_dependencies: item.risk_and_dependencies,
                  strategies: [...item.strategies],
                  recommendation_id: [item.recommendation_id],
                  recommendation: [item.recommendation],
                  recommendation_detail: [item.recommendation_detail],
                  finding_id: item.finding_id, // Tek değer
                  sections: matchingSections,
                  id: [item.id],
                };
              } else {
                acc[type][key].benefits.push(...item.benefits);
                acc[type][key].risk_and_dependencies.push(
                  ...item.risk_and_dependencies
                );
                acc[type][key].strategies.push(...item.strategies);
                acc[type][key].recommendation_id.push(item.recommendation_id);
                acc[type][key].recommendation.push(item.recommendation);
                acc[type][key].sections = [
                  ...new Set([...acc[type][key].sections, ...matchingSections]), // Unique section'ları güncelle
                ];
              }
            }

            return acc;
          },
          {}
        );

      // groupedByType'ı state olarak güncelle
      setGroupedByType(newGroupedByType);
    }
  }, [findings]);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [isPrinting, setIsPrinting] = useState(false);

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "AwesomeFileName",
    // onBeforePrint:()=>setIsPrinting(true),
    // onAfterPrint:()=>setIsPrinting(false)
  });
  
  const contents = [
    { page: "01", title: "ScoreCard" },
    { page: "02", title: "Strengths & Weaknesses" },
    { page: "03", title: "Key Findings And Observations" },
    { page: "04", title: "How to Address the Findings" },
    { page: "05", title: "Recommendation to Implement Solution Domain" },
    { page: "06", title: "How the Recommendations are Prioritized" },
    { page: "07", title: "Recommendation Prioritization" },
    { page: "08", title: "Gap Analaysis Reports" },
  ];

  const PAGE_HEIGHT = 800; // Maksimum yükseklik
  const keyFindingsAndObservationsContentRef = useRef(null);
  const recommendationContentRef = useRef(null);
  const rowRefs = useRef(new Map()); // Her satırın yüksekliğini saklamak için
  const keyFindingsRowsRef = useRef([]);
  const recommendationRowsRef = useRef([]);
  const [
    isMeasuredKeyFindingsObservationsPage,
    setIsMeasuredKeyFindingsObservationsPage,
  ] = useState(false);
  const [isMeasuredRecommendationPage, setIsMeasuredRecommendationPage] = useState(false);
  const [recommendationPages, setRecommendationPages] = useState([]);
  const [findingsPages, setFindingsPages] = useState([]);
  const bubbleContentHeights = useRef({}); // Her rec için yükseklikleri saklar
  const [isHeightCalculated, setIsHeightCalculated] = useState(false); // Yükseklik hesaplandı mı?

  useLayoutEffect(() => {
    if(groupedFindings && Object.keys(groupedFindings).length > 0 ){
      setTimeout(measureAndDivideContent, 3000); 
      setIsMeasuredKeyFindingsObservationsPage(true);
    }
// Ensure the DOM is fully rendered before measuring
  }, [keyFindingsAndObservationsContentRef]);
  useLayoutEffect(() => {
if (mappedData !== undefined){
    setTimeout(() => {
      measureAndDivideRecommendationContent();
      setIsMeasuredRecommendationPage(true); 
    }, 1000); // DOM’un tam oturmasını beklemek için 1 saniye beklet
  }
  }, [recommendationContentRef]); // Ya
  useEffect(() => {
    if (isMeasuredKeyFindingsObservationsPage) {
      requestAnimationFrame(() => {
        measureAndDivideContent();
      });
      
    }
  }, [isMeasuredKeyFindingsObservationsPage, keyFindingsRowsRef]); // Bu `useEffect` durumu izler
  useEffect(() => {

    if (isMeasuredRecommendationPage) {

      requestAnimationFrame(() => {
        measureAndDivideRecommendationContent();

        // Artık tekrar ölçmeye gerek yok
      });
    }
  }, [isMeasuredRecommendationPage, recommendationRowsRef]);
  // Bu `useEffect` durumu izler


  const measureAndDivideContent = () => {
    try {
      const allItems = [];
      Object.entries(groupedFindings).forEach(([section, items]) => {
        items.forEach((item) => {
          allItems.push(item);
        });
      });
      let tempPages = [];
      let currentPage = [];
      let currentHeight = 0;

      allItems.forEach((item, index) => {
        const rowHeight = keyFindingsRowsRef.current[index]
          ? keyFindingsRowsRef.current[index].getBoundingClientRect().height
          : 130; // Varsayılan 50px
        if (currentHeight + rowHeight > PAGE_HEIGHT) {
          tempPages.push(currentPage);
          currentPage = [];
          currentHeight = 0;
        }

        currentPage.push(item);
        currentHeight += rowHeight;
      });

      if (currentPage.length) tempPages.push(currentPage);
      setFindingsPages(tempPages);
    } catch (error) {
      // console.error("Error in measureAndDivideContent:", error);
    }
  };
  const measureAndDivideRecommendationContent = () => {
    try {
      const allItems = [];
      mappedData.forEach(({ section_name, recommendations }) => {
        allItems.push({
          section_name: section_name,
          recommendations: recommendations.map((rec) => rec), // Tüm önerileri bir dizi olarak al
        });
      });
      let tempPages = [];
      let currentPage = [];
      let currentHeight = 0;

      allItems.forEach((item, index) => {
        const rowHeight = recommendationRowsRef.current[index]
          ? recommendationRowsRef.current[index].getBoundingClientRect().height
          : 120; // Varsayılan 50px
        if (currentHeight + rowHeight > PAGE_HEIGHT) {
          tempPages.push(currentPage);
          currentPage = [];
          currentHeight = 0;
        }

        currentPage.push(item);
        currentHeight += rowHeight;
      });

      if (currentPage.length) tempPages.push(currentPage);
      setIsMeasuredRecommendationPage(false);
      setRecommendationPages(tempPages);
    } catch (error) {
     
    }
  };

  const strengthsAndWeaknessesRowRefs = useRef(new Map());
  const swRef = useRef(null);
  const [swPages, setSwPages] = useState([]);
  const [isSwMeasured, setIsSwMeasured] = useState(false);

  // İlk ölçümü yap
  useEffect(() => {
    setTimeout(measureAndDivideSwContent, 100);
  }, [strengths, weaknesses]);

  // Gerçek yüksekliklerle sayfaları tekrar hesapla
  useEffect(() => {
    if (swPages.length > 0 && isSwMeasured) {
      setTimeout(() => {
        measureAndDivideSwContent();
        setIsSwMeasured(true);
      }, 100);
    }
  }, [swPages]);

  const measureAndDivideSwContent = () => {
    let allItems = [];

    // Her Strengths ve Weaknesses elemanını eşleşecek şekilde sıralıyoruz
    const maxLength = Math.max(strengths.length, weaknesses.length);
    for (let i = 0; i < maxLength; i++) {
      allItems.push({
        strength: strengths[i] || null,
        weakness: weaknesses[i] || null,
      });
    }

    let contentPages = [];
    let currentPage = [];
    let currentHeight = 0;

    allItems.forEach((item, index) => {
      const strengthHeight =
        strengthsAndWeaknessesRowRefs.current.get(`s-${index}`)?.offsetHeight ||
        70;
      const weaknessHeight =
        strengthsAndWeaknessesRowRefs.current.get(`w-${index}`)?.offsetHeight ||
        70;
      const rowHeight = Math.max(strengthHeight, weaknessHeight);
      if (currentHeight + rowHeight > PAGE_HEIGHT) {
        contentPages.push(currentPage);
        currentPage = [];
        currentHeight = 0;
      }

      currentPage.push(item);
      currentHeight += rowHeight;
    });

    if (currentPage.length) contentPages.push(currentPage);
    setSwPages(contentPages);
  };
  const [bubbleDetailPages, setBubbleDetailPages] = useState([]);
  const splitIntoPages = () => {
    let newPages = [];

    // Her bir type için sayfa oluştur
    Object.entries(groupedByType).forEach(([type, items]) => {
      let currentPage = [];
      let currentHeight = 0;

      // Her type içindeki öğeleri sayfalara ayır
      Object.entries(items).forEach(([key, rec]) => {
        const contentHeight = calculateContentHeight(rec); // İçeriğin yüksekliğini hesapla
        if (currentHeight + contentHeight >= PAGE_HEIGHT) {
          // Sayfa sınırını geçtiyse mevcut sayfayı kaydet ve yeni sayfaya geç
          if (currentPage.length > 0) {
            newPages.push({ type, content: currentPage });
          }
          currentPage = []; // Yeni sayfa başlat
          currentHeight = 0; // Yüksekliği sıfırla
        }

        // Mevcut öğeyi ekle
        currentPage.push({ key, rec });
        currentHeight += contentHeight;
      });

      // Sayfanın sonuna gelindiğinde kalanları ekle
      if (currentPage.length > 0) {
        newPages.push({ type, content: currentPage });
      }
    });

    setBubbleDetailPages(newPages);
  };
  useEffect(() => {
    // groupedByType değişmeden önceki değeri kontrol et
    if (
      JSON.stringify(groupedByType) !==
        JSON.stringify(prevGroupedByType.current) &&
      !isHeightCalculated
    ) {
      prevGroupedByType.current = groupedByType;
      splitIntoPages();
    }
  }, [groupedByType]);

  useEffect(() => {
    if (isHeightCalculated) {
      splitIntoPages();
    }
  }, [isHeightCalculated]); // Yalnızca yükseklik hesaplandıysa çağır

  useEffect(() => {
    if (bubbleDetailPages.length > 0 && !isHeightCalculated) {
      setTimeout(() => {
        const newHeights = {};
        document.querySelectorAll(".bubble-content").forEach((el) => {
          const id = el.getAttribute("data-id");
          if (id) {
            newHeights[id] = el.clientHeight;
          }
        });

        bubbleContentHeights.current = newHeights;
        if (!isHeightCalculated) {
          setIsHeightCalculated(true);
        } // Yükseklikler hesaplandı
        // Sayfaları tekrar oluştur (bu kez gerçek yüksekliklerle)
      }, 100); // Kısa bir gecikme ile gerçek yükseklikleri al
    }
  }, [bubbleDetailPages]);

  const calculateContentHeight = (rec) => {
    return bubbleContentHeights.current[rec.id[0]] || 200; // Varsayılan değer olarak 200px
  };

  return (
    <div
      className={`flex flex-col w-full px-16 space-y-0 ${
        themeMode === "dark"
          ? "bg-[#1a1a28] text-white"
          : "bg-[#fbfbfc] text-black"
      }`}
    >
      <div className="flex w-full justify-end">
        <button
          onClick={() => handlePrint()}
          className="flex items-center gap-2 w-32 mt-2 bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700 transition-all duration-300"
        >
          <ArrowDownTrayIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Save as pdf</span>
        </button>
      </div>
      <div
        className={`pdf-content px-2 w-[100%] ${
          themeMode === "dark"
            ? "bg-[#1a1a28] text-white"
            : "bg-[#fbfbfc] text-black"
        }`}
        id="pdf-content"
        ref={componentRef}
      >
        <div className="w-full p-6 content-page flex flex-col min-h-screen space-y-16">
          {/* H1 Sayfanın Başında */}
          <h1 className="text-5xl font-bold text-[#44a5e4]">Contents</h1>

          {/* İçerik Alanı - Kalan Yüksekliği Kaplar ve Ortalanır */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 w-full">
            {contents.map((item, index) => (
              <div
                key={`contents-${index}`}
                className="flex items-center w-full space-x-4 relative"
              >
                <span className="text-[48px] font-bold text-[#44a5e4] border-b-4 border-purple-600">
                  {item.page}
                </span>
                <span
                  className={`text-lg ${
                    themeMode === "dark" ? "text-gray-300" : "text-gray-900"
                  }`}
                >
                  {item.title}
                </span>
                <div className="flex-1 mt-2"></div>

                <div className="absolute bottom-0 left-[1.55rem] w-full h-px bg-gray-600"></div>
              </div>
            ))}
          </div>
        </div>

        {currentSurveyInsightData?.report_detail?.scores !== undefined && (
          <div className="flex flex-col page-break ">
            {/* <div className="print-header">header</div> */}
            <div className="flex justify-start w-full pr-4 flex-col 2xl:flex-row ">
              <div className="w-[100%] flex flex-col">
                <h1
                  className={`inline-flex flex-wrap items-center  text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold   mb-10 ${
                    themeMode === "dark" ? "text-white" : "text-black"
                  }`}
                >
                  <span className="text-[#44a5e4] truncate mr-2">
                    {currentSurveyInsightData?.survey_name} SCORECARD
                  </span>
                </h1>
                <div className="flex-col flex lg:flex-row gap-4">
                  <div className="items-center lg:text-start flex-1  mt-4 flex ">
                    <div
                      className={`shadow-md rounded-lg rounded-l-none pt-2 px-0 pb-0 lg:w-{[400px]} h-[150px] sm:min-w-[150px] w-full  
                          bg-white text-gray-800 cursor-pointer "
                            `}
                    >
                      <div className="p-4 py-1 rounded-lg space-y-0">
                        <div className="flex justify-between items-center mb-0">
                          <span className="size-5 whitespace-nowrap text-lg font-bold text-[#1a1a28] flex flex-grow">
                            OVERALL SCORE
                          </span>
                        </div>
                        <div className="flex justify-between items-center mb-0 flex-wrap">
                          <p className="text-[30px] sm:text-[64px] font-bold text-[#1165c5] ">
                            {" "}
                            {getDisplayScore(
                              currentSurveyInsightData?.report_detail?.scores?.overall
                            ).toFixed(2)}
                          </p>
                          <span
                            className="size-5 whitespace-nowrap font-bold text-xs sm:text-[20px] text-[#4a4a4a] flex flex-grow justify-end min-w-0 text-ellipsis"
                            title={
                              getActiveStage(
                                currentSurveyInsightData?.report_detail?.scores?.overall
                              ).name
                            }
                          >
                            {(() => {
                              const stageName = getActiveStage(
                                currentSurveyInsightData?.report_detail?.scores?.overall
                              ).name;
                              return stageName.length > 10
                                ? stageName.substring(0, 10) + "..."
                                : stageName;
                            })()}
                          </span>
                        </div>
                        <div className="relative w-full bg-transparent h-[13px]">
                          <div
                            className="bg-custom-gradient h-[13px]"
                            style={{ width: `${leftPercentage}%` }}
                          ></div>
                          <div
                            className="absolute bg-custom-repeating-gradient top-[0px] h-[13px] z-20"
                            style={{
                              left: `${leftPercentage}%`,
                              width: `${100 - leftPercentage}%`,
                            }}
                          ></div>
                          <div
                            className="absolute top-[-8px] h-[5rem] w-10 transform -translate-x-1/2 z-20"
                            style={{ left: `${leftPercentage}%` }}
                          >
                            <DownIcon
                              className={`w-8 h-8 `}
                              stroke="2"
                              fill="#fff"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-[60%] mt-4 pr-4 flex flex-col items-center">
                    <div
                      className={`shadow-md rounded-lg pt-2 px-0 pb-0 w-full  lg:w-full min-w-[150px] h-[150px]
                            ${
                              themeMode === "dark"
                                ? "bg-[#000000] hover:bg-[#100c1a] text-white cursor-pointer "
                                : "bg-white text-gray-800 cursor-pointer"
                            }
                              
                            `}
                    >
                      <div className="p-4 py-1 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center">
                            <span
                              className={`size-5 whitespace-nowrap text-sm sm:text-[20px] font-bold ${
                                themeMode === "dark"
                                  ? "text-[#FFFFFF]"
                                  : "text-gray-800"
                              }`}
                            >
                              Stages
                            </span>
                          </div>
                        </div>
                        <div
                          className={` sm:block overflow-x-auto overflow-y-hidden   flex justify-center sm:justify-start   mt-8  h-[50px]  px-0 w-full`}
                        >
                          <div
                            aria-label="Tabs"
                            className={`  flex items-center w-full flex-nowrap flex-shrink-0 min-w-0 gap-[0.25rem] rounded-[0.375rem] flex-1   ${
                              themeMode === "dark"
                                ? "bg-[#a5bdff0d]"
                                : "bg-[#1530720d] "
                            }`}
                          >
                            {stages.map((stage, index) => (
                              <div
                                key={`${stage.name}-${index}`}
                                className={classNames(
                                  stage.active
                                    ? themeMode === "dark"
                                      ? "bg-[#19b394] text-[#FFFFFF] flex justify-center items-center flex-nowrap flex-shrink-0  hover:text-[#FFFFFF]"
                                      : "bg-[#A5BDFF26] text-[#5a667e] flex justify-center items-center flex-nowrap flex-shrink-0  hover:text-[#000] "
                                    : themeMode === "dark"
                                    ? " bg-[#292d32] flex justify-center items-center flex-nowrap flex-shrink-0 hover:rounded-[0.375rem] text-[#C1C9D6] hover:bg-[#a5bdff10] hover:text-[#FFFFFF]"
                                    : "flex justify-center items-center flex-nowrap flex-shrink-0 hover:rounded-[0.375rem] text-[#5a667e] hover:bg-gray-200 hover-text-[#000]",
                                  // (Object.keys(currentSurveyResult).length === 0 && tab.name === "Insight") ? "cursor:auto pointer-events-none opacity-50" : "",
                                  "group inline-flex items-center  py-2 pl-2 pr-2 text-xs sm:text-sm font-medium w-auto flex flex-1 h-[50px] "
                                )}
                              >
                                <span>{stage.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center  w-full flex-col">
              <div className="flex flex-col space-y-8 w-full border-gray-400 pt-8">
                {currentSurveyInsightData?.report_detail?.scores?.sections?.map(
                  (item, index) => {
                    const currentLeft = `${(item.score / 5) * 100}%`;
                    const targetScore =currentSurveyInsightData?.report_detail?.target_scores?.sections?.[index]?.score ?? 5;
              
                  const targetLeft = `${(targetScore / 5) * 100}%`;
                    return (
                      <div key={`aim-score-${item.name}-${index}`} className={`flex items-center w-full aim-score-${item.name}-${index}`}>
                        {/* Left Section */}
                        <div className=" w-32 md:w-64 flex items-center justify-between space-x-3 md:space-x-3">
                          <span
                            className={`${
                              themeMode === "dark"
                                ? "text-[#44a5e4]"
                                : "text-[#44a5e4]"
                            } font-bold text-md md:text-lg w-2/3`}
                          >
                            {item.name}
                          </span>
                          {/* <div className="w-10 h-10 flex  items-center justify-center rounded-full bg-gray-200 text-gray-800 font-semibold">
              {item.score.toFixed(2)}
            </div> */}
                        </div>

                        {/* Middle Line */}
                        <div className="relative flex-grow flex-1">
                          {/* Middle Line (Çizgi) */}
                          <div
                            className="absolute top-1/2 w-full border-t border-gray-400"
                            style={{ zIndex: 0 }}
                          ></div>

                          {/* Current State Value (Bubble) */}
                          <div
                            className="absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-400 text-gray-800 font-semibold"
                            style={{ left: currentLeft, top: "50%", zIndex: 2 }}
                          >
                            {getDisplayScore(item.score).toFixed(2)}
                          </div>

                          {/* Current State Label (Tam Bubble'ın Üstüne) */}
                          <div
                            className={`absolute -translate-x-1/2 -translate-y-full ${
                              themeMode === "dark"
                                ? "text-[#44a5e4]"
                                : "text-[#44a5e4]"
                            } text-xs w-20 md:text-sm font-semibold`}
                            style={{
                              left: currentLeft,
                              bottom: "100%",
                              zIndex: 3,
                            }}
                          >
                            Current State
                          </div>
                        </div>

                        {/* Future State Section */}
                        <div className="relative flex-grow flex-1">
                          {/* Middle Line (Çizgi) */}
                          <div
                            className="absolute top-1/2 w-full border-t border-gray-400"
                            style={{ zIndex: 0 }}
                          ></div>

                          {/* Current State Value (Bubble) */}
                          <div
                            className="absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-400 text-gray-800 font-semibold"
                            style={{ left: targetLeft, top: "50%", zIndex: 2 }}
                          >
                            {getDisplayScore(targetScore).toFixed(2)}
                          </div>

                          {/* Current State Label (Tam Bubble'ın Üstüne) */}
                          <div
                            className={`absolute -translate-x-1/2 -translate-y-full ${
                              themeMode === "dark"
                                ? "text-[#44a5e4]"
                                : "text-[#44a5e4]"
                            } text-xs w-20 md:text-sm font-semibold`}
                            style={{
                              left: targetLeft,
                              bottom: "100%",
                              zIndex: 3,
                            }}
                          >
                            Target State
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        )}

        {!isPrinting && <Line className="no-print" />}

        <div>
          {currentSurveyInsightData?.report_detail?.scores !== undefined &&
            swPages.map((page, pageIndex) => (
              <div key={`sw-pages-${pageIndex}`} className="flex w-full flex-col page-break" ref={swRef}>
                <div className="flex-1 flex flex-col xl:flex-row space-x-0 w-full justify-between">
                  <div className="w-full xl:w-2/5 flex-col flex ">
                    <h1
                      className={`inline-flex flex-wrap items-center  text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold   mb-10 ${
                        themeMode === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      <span className="text-[#44a5e4] truncate mr-2 ">
                        STRENGTHS & WEAKNESSES
                      </span>
                    </h1>
                    <ul
                      className={` text-[14px] sm:text-[18px]  divide-y ${
                        themeMode === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      {strengths === undefined ? (
                        <li
                          className={`  text-[12px] sm:text-[18px]  h-[100px]  ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          Güçlü yönler yükleniyor...
                        </li> //Veri henüz gelmediyse yükleniyor yazdır
                      ) : strengths.length > 0 ? (
                        page.map((item, index) => (
                          <li
                            ref={(el) => rowRefs.current.set(`s-${index}`, el)}
                            className="flex py-8  h-[100px] "
                            key={`s-${index}`}
                          >
                            {item.strength ? (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="1.5"
                                  stroke="green"
                                  className="size-6 mr-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z"
                                  />
                                </svg>

                                {item.strength}
                              </>
                            ) : (
                              <span className="opacity-50">-</span>
                            )}
                          </li>
                        ))
                      ) : (
                        <li>Güçlü yön bulunamadı.</li> // Veri geldi ama içinde güç bulunamadıysa
                      )}
                    </ul>
                  </div>
                  <div className="w-full xl:w-2/5 flex-col flex">
                    <h1
                      className={`inline-flex flex-wrap items-center  text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold   mb-10 ${
                        themeMode === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      <span className="text-[#44a5e4] truncate mr-2">
                        &nbsp;
                      </span>
                    </h1>
                    <ul
                      className={`  text-[14px] sm:text-[18px] divide-y ${
                        themeMode === "dark" ? "text-white" : "text-black"
                      }`}
                    >
                      {weaknesses === undefined ? (
                        <li
                          className={`  text-[12px] sm:text-[18px]  ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          Zayıf yönler yükleniyor...
                        </li> // Veri henüz gelmediyse yükleniyor yazdır
                      ) : weaknesses.length > 0 ? (
                        page.map((item, index) => (
                          <li
                            ref={(el) => rowRefs.current.set(`w-${index}`, el)}
                            className="flex py-8  h-[100px] "
                            key={`w-${index}`}
                          >
                            {item.weakness ? (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="1.5"
                                  stroke="red"
                                  className="size-6 mr-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M7.498 15.25H4.372c-1.026 0-1.945-.694-2.054-1.715a12.137 12.137 0 0 1-.068-1.285c0-2.848.992-5.464 2.649-7.521C5.287 4.247 5.886 4 6.504 4h4.016a4.5 4.5 0 0 1 1.423.23l3.114 1.04a4.5 4.5 0 0 0 1.423.23h1.294M7.498 15.25c.618 0 .991.724.725 1.282A7.471 7.471 0 0 0 7.5 19.75 2.25 2.25 0 0 0 9.75 22a.75.75 0 0 0 .75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 0 0 2.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384m-10.253 1.5H9.7m8.075-9.75c.01.05.027.1.05.148.593 1.2.925 2.55.925 3.977 0 1.487-.36 2.89-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 0 0 .303-.54"
                                  />
                                </svg>

                                {item.weakness}
                              </>
                            ) : (
                              <span className="opacity-50">-</span>
                            )}
                          </li>
                        ))
                      ) : (
                        <li>Zayıf yön bulunamadı.</li> // Veri geldi ama içinde güç bulunamadıysa
                      )}
                    </ul>
                  </div>
                </div>
                <Line />
              </div>
            ))}
        </div>

        <div className="w-full flex flex-col">
          {findingsPages.map((page, pageIndex) => (
            <div key={`key-findings-${pageIndex}`} className={`page-break table-print key-findings-${pageIndex}`}>
              <h1 className="text-[30px] font-bold text-[#44a5e4] mb-8">
                KEY FINDINGS AND OBSERVATIONS
              </h1>
              <div className="flex flex-row lg:grid lg:grid-cols-5 bg-[#44a5e4] text-white font-bold">
                {[
                  "SECTION",
                  "KEY FINDINGS",
                  "DESCRIPTION",
                  "HURDLES TO OVERCOME",
                  "IMPLICATIONS",
                ].map((title, index) => (
                  <div key={`key-findings-header-${index}`} className="p-4">
                    {title}
                  </div>
                ))}
              </div>

              <div
                className="border border-gray-300 divide"
                ref={keyFindingsAndObservationsContentRef}
              >
                {page.map((item, index) => {
                  const uniqueIndex = keyFindingsGlobalIndex; // Mevcut global index'i al
                  keyFindingsGlobalIndex++; // Bir sonraki öğe için artır

                  return (
                    <div
                      key={`key-findings-page-${index}`}
                      id={`key-findings-page-${index}`}
                      ref={(el) =>
                        (keyFindingsRowsRef.current[uniqueIndex] = el)
                      }
                      className={`flex flex-row lg:grid lg:grid-cols-5 divide-x  ${index ===
                        page.length - 1
                         ? "":"border-[#44a5e4] border-b border-dashed"} divide-[#44a5e4] divide-dashed`}
                    >
                      <div className="p-4 ">{item.section_name}</div>
                      <div className="p-4">{item.finding}</div>
                      <div className="p-4">{item.finding_detail}</div>
                      <div className="p-4">
                        {item.finding_hurdles_to_overcome}
                      </div>
                      <div className="p-4">{item.finding_implications}</div>
                    </div>
                  );
                })}
              </div>
              <Line className="no-print" />
            </div>
          ))}
        </div>

        <Graph
          themeMode={themeMode}
          data={currentSurveyInsightData?.report_detail}
        />
        <div className="px-0 w-full  page-break ">
          {recommendationPages.map((page, pageIndex) => (
            <div key={`reco-to-implement-solution-${pageIndex}`} className={`page-break reco-to-implement-solution-${pageIndex}`}>
              <h1
                className={`inline-flex flex-wrap items-center text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold  mb-10 ${
                  themeMode === "dark" ? "text-[#44a5e4]" : "text-[#44a5e4]"
                }`}
              >
                RECOMMENDATION TO IMPLEMENT SOLUTION DOMAIN
              </h1>
              <div className="flex w-full">
                <h2
                  key={`reco-to-implement-solution-solution-domain-${pageIndex}`}
                  className={`text-md md:text-lg font-bold mr-[1px] ${
                    themeMode === "dark"
                      ? "bg-black  text-white"
                      : "text-black bg-white shadow-lg  border-r border-l border-gray-300 border-2"
                  } p-2 w-1/3 text-center`}
                >
                  SOLUTION DOMAIN
                </h2>
                <div className="flex-1 flex justify-center">
                  <h2
                    key={`reco-to-implement-solution-recommendation-${pageIndex}`}
                    className={`text-md  md:text-lg font-bold  ${
                      themeMode === "dark"
                        ? "bg-black  text-white"
                        : "text-black bg-white shadow-lg  border-r border-l border-gray-300 border-2"
                    } p-2 w-full xl:w-full text-center border-l border-gray-300`}
                  >
                    RECOMMENDATIONS
                  </h2>
                </div>
              </div>
              <div
                className="border border-gray-300"
                id={`reco-to-implement-solution-body-container-${pageIndex}`}
                key={`reco-to-implement-solution-body-container-${pageIndex}`}
                ref={recommendationContentRef}
              >
                {page.map(({ section_name, recommendations }, sectionIndex) => {
                  const uniqueIndex = recommendationGlobalIndex; // Mevcut global index'i al
                  recommendationGlobalIndex++; // Bir sonraki öğe için artır
                  return (
                    <div
                    id={`reco-to-implement-solution-body-${uniqueIndex}`}
                      key={`reco-to-implement-solution-body-${uniqueIndex}`}
                      className="flex"
                      ref={(el) =>
                        (recommendationRowsRef.current[uniqueIndex] = el)
                      }
                    >
                      <div
                        className={`w-1/3 p-4 text-white  flex items-center justify-center text-xs sm:text-sm md:text-lg`}
                        style={{
                          backgroundColor: getColorForIndex(
                            sectionIndex,
                            mappedData?.length ?? 1
                          ),
                        }}
                      >
                        {section_name}
                      </div>
                      <div className="w-2/3 p-4 border-l border-gray-300">
                        <ul className="list-disc pl-4">
                          {recommendations.map((rec, idx) => (
                            <li
                              key={`reco-detail-${idx}`}
                              className={`text-xs sm:text-sm ${
                                themeMode === "dark"
                                  ? "text-gray-200"
                                  : "text-gray-700"
                              }`}
                            >
                              {rec.recommendation_detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Line className="no-print" />
            </div>
          ))}
        </div>
        <div>
          <div className="flex flex-col  w-full px-0 overflow-hidden justify-center  page-break ">
            <h1
              className={`inline-flex flex-wrap items-center text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold my-8 ${
                themeMode === "dark" ? "text-[#44a5e4]" : "text-[#44a5e4]"
              }`}
            >
              HOW THE RECOMMENDATIONS ARE PRIORITIZED
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start w-full relative CENTER-CONTENT">
              {steps.map((step, index) => (
                <div
                  key={`${step.title}-${index}`}
                  className="flex flex-col items-center text-center w-full justify-start h-auto min-h-[300px]"
                >
                  <div className="relative flex items-center justify-center w-48 h-48 rounded-full border-8 border-gray-200 z-10 mb-4">
                    <div
                      className={`w-44 h-44 flex items-center justify-center rounded-full text-white font-bold text-[32px] absolute ${step.bgColor}`}
                    >
                      {step.number}
                    </div>
                  </div>
                  {index !== steps.length - 1 && (
                    <div className="absolute top-24 w-full left-1 h-0 lg:border-t-2 lg:border-dashed lg:border-gray-400 z-1"></div>
                  )}
                  <div className="flex flex-col items-center text-center min-h-[120px]">
                    <h3
                      className={`${
                        themeMode === "dark"
                          ? "text-[#44a5e4]"
                          : "text-[#44a5e4]"
                      } font-bold text-md sm:text-lg  mb-2`}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`${
                        themeMode === "dark" ? "text-white" : "text-gray-600"
                      } text-base max-w-xs text-center`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Line />
          {/* <TimelineChart themeMode={themeMode} /> */}
          <div className=" w-full px-0 pt-8 flex flex-col page-break ">
            <h1
              className={`inline-flex flex-wrap items-center  text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold   mb-10 ${
                themeMode === "dark" ? "text-white" : "text-black"
              }`}
            >
              <span className="text-[#44a5e4] truncate mr-2">
                RECOMMENDATION PRIORITIZATION
              </span>
            </h1>
            <div className="w-full flex items-center flex-row ">
              <div className="w-2/3">
                <ul
                  className={`space-y-0 divide-y h-full text-base ${
                    themeMode === "dark" ? "divide-gray-200" : "divide-gray-500"
                  }`}
                >
                  {currentSurveyInsightData?.report_detail?.recommendations
                    .filter((x) => x.type === "Quick Wins")
                    .map((rec, index) => (
                      <li
                        key={`reco-prioritization-quick-${index}`}
                        className={`px-0 bg-[#fff] divide-x border flex  h-full ${
                          themeMode === "dark" ? "text-black" : "text-black "
                        } `}
                      >
                        <span className="px-2 py-1 w-10">{rec.id}</span>
                        <div className="px-2 py-1 flex flex-1">
                          {" "}
                          {rec.recommendation}
                        </div>
                      </li>
                    ))}
                  {currentSurveyInsightData?.report_detail?.recommendations
                    .filter((x) => x.type === "Critical Enablers")
                    .map((rec, index) => (
                      <li
                        key={`reco-prioritization-critical-${index}`}
                        className={`px-0 bg-[#cd80ff] divide-x border flex  h-full ${
                          themeMode === "dark" ? "text-black" : "text-black "
                        } `}
                      >
                        <span className="px-2 py-1 w-10">{rec.id}</span>
                        <div className="px-2 py-1 flex flex-1">
                          {" "}
                          {rec.recommendation}
                        </div>
                      </li>
                    ))}
                  {currentSurveyInsightData?.report_detail?.recommendations
                    .filter((x) => x.type === "Long Term Initiatives")
                    .map((rec, index) => (
                      <li
                        key={`reco-prioritization-longterm-${index}`}
                        className={`px-0 bg-[#92d050] divide-x border flex  h-full ${
                          themeMode === "dark" ? "text-black" : "text-black "
                        } `}
                      >
                        <span className="px-2 py-1 w-10">{rec.id}</span>
                        <div className="px-2 py-1 flex flex-1">
                          {" "}
                          {rec.recommendation}
                        </div>
                      </li>
                    ))}
                  {currentSurveyInsightData?.report_detail?.recommendations
                    .filter((x) => x.type === "De-prioritize")
                    .map((rec, index) => (
                      <li
                        key={`reco-prioritization-deprioritize-${index}`}
                        className={`px-0 bg-[#828282] divide-x border flex  h-full ${
                          themeMode === "dark" ? "text-black" : "text-black "
                        } `}
                      >
                        <span className="px-2 py-1 w-10">{rec.id}</span>
                        <div className="px-2 py-1 flex flex-1">
                          {" "}
                          {rec.recommendation}
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
              {currentSurveyInsightData?.report_detail?.recommendations !==
                undefined && (
                <BubbleChart
                  className="w-[650px] mt-2"
                  themeMode={themeMode}
                  data={
                    currentSurveyInsightData?.report_detail?.recommendations
                  }
                />
              )}
            </div>
          </div>
          <Line />
          <div>
            {bubbleDetailPages.map((page, pageIndex) => (
              <div className="page-break" key={`bubble-detail-${pageIndex}`}>
                {page.content.map(({ type, rec }, index) => {
                  const uniqueIndex = bubbleDetailCardsGlobalIndex; // Mevcut global index'i al
                  bubbleDetailCardsGlobalIndex++; // Bir sonraki öğe için artır

                  return (
                    <div key={`bubble-detail-page-${index}`} className=" page-breaks">
                      {/* Başlık her sayfada tekrar edilir */}
                      {index === 0 && (
                        <h1
                          className={`inline-flex flex-wrap items-center text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold mb-10 ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          <span className="text-[#44a5e4] truncate mr-2 uppercase">
                            {page.type}
                          </span>
                        </h1>
                      )}

                      <div
                        className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4 bubble-content"
                        key={`bubble-detail-page-card-body-${index}`}
                        data-id={uniqueIndex}
                      >
                        <div className="border p-4 rounded-lg shadow-lg">
                          <div className="flex items-center mb-2">
                            <span
                              className={`${
                                themeMode === "dark"
                                  ? "bg-[#44a5e4]"
                                  : "bg-[#44a5e4]"
                              } text-white px-3 py-1 rounded-md font-bold text-lg mr-2`}
                            >
                              {Array.isArray(rec.id)
                                ? rec.id.join(", ")
                                : rec.id}{" "}
                            </span>
                            {rec.recommendation.map((rec, i) => (
                              <h2
                               key={`recHeader-${i}`}
                                className={`font-bold text-lg ${
                                  themeMode === "dark"
                                    ? "text-white"
                                    : "text-black"
                                }`}
                              >
                                {Array.isArray(rec) ? rec.join(", ") : rec}{" "}
                              </h2>
                            ))}
                          </div>
                          <h3
                            className={`font-bold mt-2 ${
                              themeMode === "dark" ? "text-white" : "text-black"
                            }`}
                          >
                            DESCRIPTION
                          </h3>
                          <p className="list-disc ml-5 text-base">
                            {rec.recommendation_detail}
                          </p>

                          <h3
                            className={`font-bold mt-2 ${
                              themeMode === "dark" ? "text-white" : "text-black"
                            }`}
                          >
                            WIN STRATEGY
                          </h3>
                          <ul className="list-disc ml-5 text-base">
                            {rec.strategies.map((item, index) => (
                              <li
                                key={`bubble-detail-win-${index}`}
                                className={`${
                                  themeMode === "dark"
                                    ? "text-white"
                                    : "text-black"
                                }`}
                              >
                                {item}
                              </li>
                            ))}
                          </ul>
                          <h3
                            className={`font-bold mt-2  ${
                              themeMode === "dark" ? "text-white" : "text-black"
                            }`}
                          >
                            BENEFITS
                          </h3>
                          <ul className="list-disc ml-5 text-base">
                            {rec.benefits.map((item, index) => (
                              <li
                                key={`bubble-detail-benefits-${index}`}
                                className={`${
                                  themeMode === "dark"
                                    ? "text-white"
                                    : "text-black"
                                }`}
                              >
                                {item}
                              </li>
                            ))}
                          </ul>
                          <h3
                            className={`font-bold mt-2  ${
                              themeMode === "dark" ? "text-white" : "text-black"
                            }`}
                          >
                            RISKS & DEPENDENCIES
                          </h3>
                          <ul className="list-disc ml-5 text-base">
                            {rec.risk_and_dependencies.map((risk, i) => (
                              <li
                                key={`bubble-detail-risk-${i}`}
                                className={`${
                                  themeMode === "dark"
                                    ? "text-white"
                                    : "text-black"
                                }`}
                              >
                                {risk}
                              </li>
                            ))}
                          </ul>
                          {/* Diğer alanlar */}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Line />
              </div>
            ))}
          </div>
        </div>
        <div className="page-break">
          {currentSurveyInsightData?.report_detail?.scores !== undefined && (
            <div className="flex w-full flex-col px-0 pb-8 ">
              <h1
                className={`inline-flex flex-wrap items-center text-center md:text-left break-words text-[20px] sm:text-[26px] md:text-[30px] lg:text-[34px] font-bold  mb-10 ${
                  themeMode === "dark" ? "text-[#44a5e4]" : "text-[#44a5e4]"
                }`}
              >
                GAP ANALYSIS REPORT
              </h1>
              <div className="flex flex-col w-full last-center-content ">
                <div className="flex w-full space-x-2">
                  <div
                    className="flex-1 text-center font-bold py-2 text-xs md:text-sm whitespace-nowrap bg-[#44a5e4] text-white"
                    style={{ flex: 0.5 }}
                  >
                    SECTION
                  </div>
                  <div
                    className="flex-1 text-center font-bold py-2 text-xs md:text-sm whitespace-nowrap bg-[#44a5e4] text-white"
                    style={{ flex: 0.5 }}
                  >
                    SCORE
                  </div>
                  <div className="flex-1 text-center font-bold py-2 text-xs md:text-sm whitespace-nowrap bg-gray-700 text-white">
                    CURRENT STATE
                  </div>
                  <div className="flex-1 text-center font-bold py-2 text-xs md:text-sm whitespace-nowrap bg-gray-700 text-white">
                    TARGET STATE
                  </div>
                </div>
                {currentSurveyInsightData?.report_detail?.scores?.sections.map(
                  (section, index) => (
                    <div key={`gap-analysis-${section.name}-${index}`} className="flex w-full space-x-2">
                      <div
                        className="flex-1 text-center items-center flex items-center justify-center"
                        style={{ flex: 0.5 }}
                      >
                        <h3
                          className={` ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          {section.name}
                        </h3>
                      </div>
                      <div
                        className="flex-1 text-center flex items-center justify-center"
                        style={{ flex: 0.5 }}
                      >
                        <div className="relative w-full h-full flex justify-center items-center text-center">
                          <svg
                            className="w-16 h-16 transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <circle
                              className="text-gray-200 stroke-current"
                              strokeWidth="4"
                              cx="18"
                              cy="18"
                              r="16"
                              fill="none"
                            />
                            <circle
                              className={`${
                                themeMode === "dark"
                                  ? "text-[#44a5e4]"
                                  : "text-[#44a5e4]"
                              } stroke-current`}
                              strokeWidth="4"
                              strokeDasharray="100, 100"
                              strokeDashoffset={`${
                                100 - (section.score / 5) * 100
                              }`}
                              cx="18"
                              cy="18"
                              r="16"
                              fill="none"
                              transform="rotate(0 18 18)"
                            />
                          </svg>
                          <div className="absolute ">
                            <span
                              className={`${
                                themeMode === "dark"
                                  ? "text-[#44a5e4]"
                                  : "text-[#44a5e4]"
                              } top-5 font-bold text-lg`}
                            >
                              {getDisplayScore(section.score).toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 text-start py-2">
                        <h3
                          className={` ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          {currentSurveyInsightData?.report_detail?.gap_analysis
                            .filter((x) => x["section_name"] === section.name)
                            ?.map((y) => y.current_state)
                            .join(" ")}
                        </h3>
                      </div>
                      <div className="flex-1 text-start py-2">
                        <h3
                          className={` ${
                            themeMode === "dark" ? "text-white" : "text-black"
                          }`}
                        >
                          {currentSurveyInsightData?.report_detail?.gap_analysis
                            .filter((x) => x["section_name"] === section.name)
                            ?.map((y) => y.target_state)
                            .join(" ")}
                        </h3>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
          {/* <div className="print-footer">footer</div> */}
        </div>
      </div>
    </div>
  );
};
export default MainReport;
